import { useCallback, useEffect, useRef, useState } from 'react';
import { HOST_API } from '../config';

// Live progress for automations *you* set off.
//
// The socket is per-user, so what arrives here is only the work your own last
// click caused. Everyone else's runs are in the activity log, which is the
// durable record. This is the ephemeral one, and it is allowed to be lossy: a
// dropped frame costs a spinner, never the work itself.
//
// Frames are merged by run_id. A frame carrying an action_slug describes one
// step; a frame without one describes the whole run.

const TERMINAL = ['success', 'failed', 'skipped'];

// How long a finished run stays on screen. Long enough to read the green line,
// short enough that the top bar goes quiet on its own.
const DISMISS_AFTER = 6000;
const DISMISS_AFTER_FAILED = 15000;

const MAX_RUNS = 6;

const isTerminal = (state) => TERMINAL.includes(state);

function applyFrame(runs, frame) {
  const runId = frame.run_id;
  if (!runId) return runs;

  const existing = runs.find((r) => r.runId === runId);

  // The queued frame is the only one that carries the step list, but it can be
  // lost or arrive late, so a run is created by whichever frame lands first.
  const base = existing || {
    runId,
    ruleId: frame.rule_id || '',
    ruleName: frame.rule_name || 'Automation',
    triggerSlug: frame.trigger_slug || '',
    subject: frame.subject || '',
    state: 'queued',
    steps: [],
    startedAt: Date.now(),
    finishedAt: null,
  };

  const next = { ...base };
  if (frame.rule_name) next.ruleName = frame.rule_name;
  if (frame.subject) next.subject = frame.subject;
  if (frame.trigger_slug) next.triggerSlug = frame.trigger_slug;

  if (Array.isArray(frame.steps) && frame.steps.length) {
    next.steps = frame.steps.map((s) => ({
      actionSlug: s.action_slug,
      name: s.name,
      icon: s.icon,
      color: s.color,
      label: s.label,
      state: s.state || 'queued',
      reason: '',
    }));
  }

  if (frame.action_slug) {
    const idx = next.steps.findIndex((s) => s.actionSlug === frame.action_slug);
    const patch = {
      actionSlug: frame.action_slug,
      state: frame.state,
      label: frame.label || '',
      reason: frame.reason || '',
    };
    if (idx === -1) {
      next.steps = [...next.steps, { name: '', icon: '', color: '', ...patch }];
    } else {
      next.steps = next.steps.map((s, i) => (i === idx ? { ...s, ...patch, label: patch.label || s.label } : s));
    }
    // A step moving is proof the run itself is under way.
    if (!isTerminal(next.state)) next.state = 'running';
  } else {
    next.state = frame.state || next.state;
    if (frame.reason) next.reason = frame.reason;
    if (isTerminal(next.state)) next.finishedAt = Date.now();
  }

  const merged = existing ? runs.map((r) => (r.runId === runId ? next : r)) : [next, ...runs];
  return merged.slice(0, MAX_RUNS);
}

export default function useAutomationProgress({ enabled = true } = {}) {
  const [runs, setRuns] = useState([]);
  const [isConnected, setIsConnected] = useState(false);

  const wsRef = useRef(null);
  const retryRef = useRef(null);
  const attemptsRef = useRef(0);
  const timersRef = useRef({});

  const scheduleDismiss = useCallback((runId, state) => {
    if (timersRef.current[runId]) clearTimeout(timersRef.current[runId]);
    timersRef.current[runId] = setTimeout(() => {
      delete timersRef.current[runId];
      setRuns((prev) => prev.filter((r) => r.runId !== runId));
    }, state === 'failed' ? DISMISS_AFTER_FAILED : DISMISS_AFTER);
  }, []);

  const dismiss = useCallback((runId) => {
    if (timersRef.current[runId]) {
      clearTimeout(timersRef.current[runId]);
      delete timersRef.current[runId];
    }
    setRuns((prev) => prev.filter((r) => r.runId !== runId));
  }, []);

  const dismissAll = useCallback(() => {
    Object.values(timersRef.current).forEach(clearTimeout);
    timersRef.current = {};
    setRuns([]);
  }, []);

  const connect = useCallback(() => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    const wsProtocol = HOST_API.startsWith('https') ? 'wss' : 'ws';
    const wsHost = HOST_API.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/ws/automations/?token=${accessToken}`;

    try {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        setIsConnected(true);
        attemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        let frame;
        try {
          frame = JSON.parse(event.data);
        } catch (err) {
          return;
        }
        if (frame.type !== 'automation_progress') return;

        setRuns((prev) => applyFrame(prev, frame));

        // Run-level terminal frames start the countdown. Step frames do not:
        // a rule with three actions has more to say after the first one lands.
        if (!frame.action_slug && isTerminal(frame.state)) {
          scheduleDismiss(frame.run_id, frame.state);
        }
      };

      ws.onclose = (event) => {
        setIsConnected(false);
        wsRef.current = null;
        // Backs off rather than hammering: nothing here is urgent enough to
        // justify a reconnect storm against a server that is already unhappy.
        if (event.code !== 1000 && attemptsRef.current < 8) {
          attemptsRef.current += 1;
          retryRef.current = setTimeout(connect, Math.min(30000, 2000 * attemptsRef.current));
        }
      };

      ws.onerror = () => {};

      wsRef.current = ws;
    } catch (err) {
      // No socket means no live view. The automation still runs.
    }
  }, [scheduleDismiss]);

  useEffect(() => {
    if (!enabled) return undefined;
    connect();
    const timers = timersRef.current;
    return () => {
      if (retryRef.current) clearTimeout(retryRef.current);
      Object.values(timers).forEach(clearTimeout);
      if (wsRef.current) {
        wsRef.current.close(1000, 'unmount');
        wsRef.current = null;
      }
    };
  }, [enabled, connect]);

  const active = runs.filter((r) => !isTerminal(r.state));

  return { runs, activeCount: active.length, isConnected, dismiss, dismissAll };
}
